import React from 'react'
import { Link, useLocation } from 'react-router-dom'

function PrivacyPolicy() {
  const isDe = useLocation().pathname.startsWith('/de')
  const homePath = isDe ? '/de' : '/'
  return (
    <div>
      <div className="terms-of-use">
        <Link to={homePath}>
          <img style={{filter: 'invert(1)'}} src="./img/bonusclub_logo.webp" alt="BonusClub" />
        </Link>
        
        <h1>Privacy Policy</h1>
        <p><strong>Last updated: February 2026</strong></p>
        
        <h2>1. General</h2>
        <p>This Privacy Policy explains how BonusClub LLC, 7901 4th St N STE 300, St. Petersburg, FL 33702, USA ("BonusClub", "we", "us") collects, uses, and protects personal data in connection with our websites (<a href="https://www.bonusclub.io" target="_blank" rel="noopener noreferrer">www.bonusclub.io</a> and <a href="https://bonusclub.link" target="_blank" rel="noopener noreferrer">bonusclub.link</a>), any associated subdomains, and our services.</p>

        <h2>2. Data Controller &amp; Data Processor Roles</h2>
        <p><strong>BonusClub as Data Controller:</strong> For personal data we collect directly (e.g., when you visit our website, book a strategy call, or create an account), BonusClub LLC acts as Data Controller within the meaning of Art. 4(7) GDPR.</p>
        <p><strong>BonusClub as Data Processor:</strong> For personal data you upload, import, or process using the Service (e.g., leads, email addresses, LinkedIn profiles), you act as Data Controller and BonusClub LLC acts exclusively as Data Processor under Article 28 GDPR, processing personal data only on your documented instructions.</p>

        <h2>3. Data We Collect as Controller</h2>
        <p>When you visit our website or use our services, we may collect:</p>
        <ul>
          <li><strong>Contact information:</strong> Name, email address, phone number, company name (when you book a call, fill out a form, or create an account).</li>
          <li><strong>Usage data:</strong> IP address (anonymized), browser type, device information, pages visited, and referral source.</li>
          <li><strong>Communication data:</strong> Content of emails or messages you send us.</li>
        </ul>
        <p><strong>Legal basis (Art. 6 GDPR):</strong></p>
        <ul>
          <li>Contract performance (Art. 6(1)(b)), to provide our services and respond to your inquiries.</li>
          <li>Legitimate interest (Art. 6(1)(f)), to improve our website, ensure security, and communicate about our services.</li>
          <li>Consent (Art. 6(1)(a)), where applicable, e.g., for marketing communications. You may withdraw consent at any time.</li>
        </ul>
        
        <h2>4. Data Processing Agreement (DPA)</h2>
        <p>For data we process on your behalf, our separate Data Processing Agreement (DPA) applies. It is incorporated by reference and available at <a href="https://www.bonusclub.io/dpa" target="_blank" rel="noopener noreferrer">https://www.bonusclub.io/dpa</a>. The DPA includes the mandatory provisions of Art. 28 GDPR and the EU Standard Contractual Clauses (2021/914/EU) for transfers to the United States.</p>
        
        <h2>5. Data We Process on Your Behalf</h2>
        <p>As Data Processor, we process personal data only as instructed by you. This may include:</p>
        <ul>
          <li>Names, job titles, business email addresses, phone numbers, LinkedIn profiles, and any other data you upload or collect through the Service.</li>
        </ul>
        
        <h2>6. Your Obligations as Customer</h2>
        <p>You represent and warrant that all personal data you process using the Service has been collected and is processed lawfully (e.g., based on legitimate interest, consent, or contract necessity under Art. 6 GDPR) and in full compliance with § 7 UWG (Germany), CAN-SPAM Act, and all other applicable laws.</p>
        
        <h2>7. Security Measures</h2>
        <p>We implement industry-standard technical and organizational measures (detailed in the DPA) including TLS 1.3 encryption in transit, AES-256 encryption at rest, strict access controls, regular security audits, and breach notification within 72 hours.</p>
        
        <h2>8. Sub-Processors</h2>
        <p>We use only vetted sub-processors listed in the DPA. You grant general authorization for their engagement.</p>
        
        <h2>9. International Data Transfers</h2>
        <p>Personal data from the EEA, UK, or Switzerland is transferred to the United States under the EU-U.S. Data Privacy Framework (DPF) and/or the EU Standard Contractual Clauses (Module 2: Controller-to-Processor).</p>
        
        <h2>10. Data Retention &amp; Deletion</h2>
        <p>We retain personal data only for as long as necessary for the purposes described in this policy or as required by law. Upon termination of your account we delete or return all personal data within 30 days, except where retention is required by law.</p>
        
        <h2>11. Your Rights (GDPR Art. 15-22)</h2>
        <p>If you are located in the EEA, you have the following rights regarding personal data we hold about you as Controller:</p>
        <ul>
          <li><strong>Access</strong> (Art. 15), obtain confirmation and a copy of your data.</li>
          <li><strong>Rectification</strong> (Art. 16), correct inaccurate data.</li>
          <li><strong>Erasure</strong> (Art. 17), request deletion of your data.</li>
          <li><strong>Restriction</strong> (Art. 18), restrict processing under certain conditions.</li>
          <li><strong>Data portability</strong> (Art. 20), receive your data in a structured, machine-readable format.</li>
          <li><strong>Objection</strong> (Art. 21), object to processing based on legitimate interest.</li>
        </ul>
        <p>To exercise these rights, contact us at <a href="mailto:julian@bonusclub.io">julian@bonusclub.io</a>. We will respond within 30 days.</p>
        <p>For data we process on your behalf (as Processor), you as Data Controller are responsible for responding to data subject requests. We will assist you as required under Art. 28 GDPR.</p>

        <h2>12. Right to Lodge a Complaint</h2>
        <p>You have the right to lodge a complaint with a data protection supervisory authority, in particular in the EU Member State of your habitual residence, place of work, or place of the alleged infringement (Art. 77 GDPR).</p>
        
        <h2>13. California Residents (CCPA/CPRA)</h2>
        <p>We do not sell personal information. California residents may contact us at <a href="mailto:julian@bonusclub.io">julian@bonusclub.io</a> for any CCPA-related requests.</p>
        
        <h2>14. Cookies &amp; Tracking</h2>
        <p>We use only strictly necessary cookies for the technical operation of the website. No advertising or cross-site tracking is performed. No consent is required for strictly necessary cookies.</p>
        
        <h2>15. Children's Privacy</h2>
        <p>Our Service is designed for businesses and is not directed to individuals under 16. We do not knowingly collect personal data from children.</p>
        
        <h2>16. Changes to This Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of material changes via email or through the Service. The date at the top of this page indicates when the policy was last updated.</p>
        
        <h2>17. Contact</h2>
        <p>BonusClub LLC<br />7901 4th St N STE 300<br />St. Petersburg, FL 33702, USA<br />Email: <a href="mailto:julian@bonusclub.io">julian@bonusclub.io</a><br />Phone: +1 (754) 276-0068</p>
        
        <h2>18. Governing Law</h2>
        <p>This Privacy Policy is governed by the laws of the State of Florida, United States. Where you are located in the EEA, mandatory provisions of EU data protection law (including the GDPR) apply in addition and take precedence where required.</p>
        
        <hr />
        
        <p><strong>© 2026 BonusClub LLC. All rights reserved.</strong></p>
      </div>
    </div>
  )
}

export default PrivacyPolicy
